import { layers, namedFlavor, type Flavor } from '@protomaps/basemaps';
import type { StyleSpecification } from 'maplibre-gl';

/**
 * Dale's own map: cream and white, laid out like OpenTable's, on Protomaps'
 * OpenStreetMap extracts served from our R2 bucket. No Google, no API key.
 */

export type MapCountry = 'PA' | 'VE';

const MAPS_ORIGIN = 'https://dale-maps.martinfurbano.workers.dev';
const BUILD = 'v20261005';

/** One extract per country Dale serves; the bounds keep the camera inside the data. */
export const DALE_MAP_REGIONS: Readonly<Record<MapCountry, { archive: string; bounds: [number, number, number, number]; center: [number, number]; zoom: number }>> = {
  PA: { archive: `${MAPS_ORIGIN}/${BUILD}/pa.pmtiles`, bounds: [-83.06, 7.15, -77.15, 9.68], center: [-79.52, 8.98], zoom: 12 },
  VE: { archive: `${MAPS_ORIGIN}/${BUILD}/ve.pmtiles`, bounds: [-73.38, 0.64, -59.8, 12.2], center: [-66.88, 10.49], zoom: 12 },
};

/** Which country's extract a point belongs to: Venezuela lies east of 75° W, Panamá west of it. */
export function countryAt(lng: number): MapCountry {
  return lng > -75 ? 'VE' : 'PA';
}

export const DALE_MAP_ATTRIBUTION = '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap</a> · <a href="https://protomaps.com" target="_blank" rel="noopener noreferrer">Protomaps</a>';

export const DALE_MAP_COLORS = { paper: '#FBF8F3', tan: '#E6D6BC', ink: '#1D1812', orange: '#F09B5C', copper: '#A34D12', muted: '#5C5347' } as const;

const LAND = '#FAF6F0';
const STREET = '#FFFFFF';
const EDGE = '#E8E0D3';
const EDGE_STRONG = '#DDD2C1';
const HIGHWAY = '#FBE7D2';
const HIGHWAY_EDGE = '#EBCDAE';
const LOT = '#F5F0E8';
const QUIET = '#F6F2EA';
const PARK = '#EDEEE3';
const TUNNEL = '#F6F1EA';
const NAME = '#8A7F72';
const NAME_SOFT = '#9A8F82';

/**
 * Laid out like OpenTable's map, dressed in Dale: cream land, white streets
 * with a warm cream edge, highways in a pale sunset, clear blue water, and
 * street and neighbourhood names in soft warm grey. Forest and fields wear
 * the land colour and parks only a whisper of sage, so the map never reads
 * green. Strong colour on the map belongs to Dale's pins.
 */
export function daleFlavor(): Flavor {
  const base = namedFlavor('light');
  return {
    ...base,
    background: LAND,
    earth: LAND,
    water: '#BCD7E8',
    park_a: PARK, park_b: PARK, zoo: PARK,
    wood_a: QUIET, wood_b: QUIET, scrub_a: QUIET, scrub_b: QUIET,
    hospital: LOT, industrial: LOT, school: LOT, military: LOT, aerodrome: LOT,
    pedestrian: '#F9F5EF', sand: '#F6EEDD', beach: '#F6EEDD', glacier: '#FFFFFF', runway: EDGE,
    landcover: { forest: QUIET, grassland: QUIET, farmland: QUIET, scrub: QUIET, barren: '#F8F3EB', urban_area: '#F5F0E8', glacier: '#FFFFFF' },
    buildings: '#F2ECE2', pier: '#F2ECE2',
    highway: HIGHWAY, major: STREET, link: STREET, minor_a: STREET, minor_b: STREET, minor_service: STREET, other: STREET, railway: '#DCD2C3',
    highway_casing_early: HIGHWAY_EDGE, highway_casing_late: HIGHWAY_EDGE, major_casing_early: EDGE_STRONG, major_casing_late: EDGE_STRONG, minor_casing: EDGE, minor_service_casing: EDGE, link_casing: EDGE,
    tunnel_highway: TUNNEL, tunnel_major: TUNNEL, tunnel_minor: TUNNEL, tunnel_link: TUNNEL, tunnel_other: TUNNEL,
    tunnel_highway_casing: EDGE, tunnel_major_casing: EDGE, tunnel_minor_casing: EDGE, tunnel_link_casing: EDGE, tunnel_other_casing: EDGE,
    bridges_highway: HIGHWAY, bridges_major: STREET, bridges_minor: STREET, bridges_link: STREET, bridges_other: STREET,
    bridges_highway_casing: HIGHWAY_EDGE, bridges_major_casing: EDGE_STRONG, bridges_minor_casing: EDGE, bridges_link_casing: EDGE, bridges_other_casing: EDGE,
    boundaries: '#CFC3B2',
    roads_label_minor: NAME_SOFT, roads_label_minor_halo: STREET,
    roads_label_major: NAME, roads_label_major_halo: STREET,
    subplace_label: NAME, subplace_label_halo: LAND,
    city_label: '#4A423A', city_label_halo: LAND,
    state_label: '#B0A597', state_label_halo: LAND,
    country_label: NAME_SOFT,
    address_label: NAME_SOFT, address_label_halo: STREET,
    ocean_label: '#6E93AB',
  };
}

/** Business names, house numbers and road icons stay off: on a Dale map the only places are Dale's pins. */
const HIDDEN_LAYERS = new Set(['pois', 'address_label', 'roads_shields', 'roads_oneway']);

/**
 * The full MapLibre style for one country: streets, neighbourhoods, towns
 * and water are named; businesses are not.
 */
export function daleMapStyle(country: MapCountry): StyleSpecification {
  const region = DALE_MAP_REGIONS[country];
  return {
    version: 8,
    glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
    sources: {
      protomaps: { type: 'vector', url: `pmtiles://${region.archive}`, attribution: DALE_MAP_ATTRIBUTION },
    },
    layers: layers('protomaps', daleFlavor(), { lang: 'es' }).filter((layer) => !HIDDEN_LAYERS.has(layer.id)),
  };
}
